import { useState, useRef, useCallback, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function PhotoUpload({ fighter, onUploaded }) {
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(fighter.photo_url || null)
  const [error, setError] = useState('')
  const [positioning, setPositioning] = useState(false)
  const [position, setPosition] = useState(fighter.photo_position || '50% 20%')
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 50, startPosY: 20 })
  const containerRef = useRef()
  const inputRef = useRef()

  // Lock page scroll when repositioning
  useEffect(() => {
    if (positioning) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    } else {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [positioning])

  async function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return }

    setError('')
    setUploading(true)
    const localUrl = URL.createObjectURL(file)
    setPreview(localUrl)

    try {
      const ext = file.name.split('.').pop()
      const path = `${fighter.id}/photo.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('fighter-photos')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (uploadError) throw uploadError

      const { data } = supabase.storage.from('fighter-photos').getPublicUrl(path)
      const publicUrl = data.publicUrl + '?t=' + Date.now()

      // Remove background via edge function
      let finalUrl = publicUrl
      try {
        const { data: bgData, error: bgError } = await supabase.functions.invoke('remove-bg', {
          body: { image_url: publicUrl }
        })
        if (bgData && bgData.base64 && !bgError) {
          const byteChars = atob(bgData.base64)
          const byteArr = new Uint8Array(byteChars.length)
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i)
          const pngBlob = new Blob([byteArr], { type: 'image/png' })
          const pngPath = fighter.id + '/photo_nobg.png'
          const { error: reupErr } = await supabase.storage
            .from('fighter-photos')
            .upload(pngPath, pngBlob, { upsert: true, contentType: 'image/png' })
          if (!reupErr) {
            const { data: pngData } = supabase.storage.from('fighter-photos').getPublicUrl(pngPath)
            finalUrl = pngData.publicUrl + '?t=' + Date.now()
          }
        }
      } catch (bgErr) {
        console.warn('Background removal failed, using original:', bgErr.message)
      }

      await supabase.from('fighters').update({
        photo_url: finalUrl,
        avatar_style: 'photo',
        photo_position: position,
      }).eq('id', fighter.id)

      setPreview(finalUrl)
      setPositioning(true)
      onUploaded && onUploaded(finalUrl)
    } catch (err) {
      setError('Upload failed — ' + err.message)
      setPreview(fighter.photo_url || null)
    }
    setUploading(false)
  }

  async function removePhoto() {
    if (!window.confirm('Remove this photo?')) return
    setUploading(true)
    await supabase.from('fighters').update({ photo_url: null, avatar_style: 'avatar', photo_position: null }).eq('id', fighter.id)
    setPreview(null)
    setPositioning(false)
    onUploaded && onUploaded(null)
    setUploading(false)
  }

  async function savePosition() {
    setSaving(true)
    await supabase.from('fighters').update({ photo_position: position }).eq('id', fighter.id)
    setSaving(false)
    setPositioning(false)
    onUploaded && onUploaded(preview)
  }

  function getCoords(e) {
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
    return { x: e.clientX, y: e.clientY }
  }

  function onDragStart(e) {
    e.preventDefault()
    e.stopPropagation()
    const coords = getCoords(e)
    const parts = position.split(' ')
    dragRef.current = {
      startX: coords.x,
      startY: coords.y,
      startPosX: parseFloat(parts[0]),
      startPosY: parseFloat(parts[1]),
    }
    setIsDragging(true)
  }

  function onDragMove(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging || !containerRef.current) return
    const coords = getCoords(e)
    const rect = containerRef.current.getBoundingClientRect()
    const dx = ((coords.x - dragRef.current.startX) / rect.width) * 100
    const dy = ((coords.y - dragRef.current.startY) / rect.height) * 100
    const newX = Math.min(100, Math.max(0, dragRef.current.startPosX - dx))
    const newY = Math.min(100, Math.max(0, dragRef.current.startPosY - dy))
    setPosition(`${newX.toFixed(1)}% ${newY.toFixed(1)}%`)
  }

  function onDragEnd(e) {
    e.preventDefault()
    setIsDragging(false)
  }

  if (positioning && preview) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
        zIndex: 9999, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
      }}>
        <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
          Position your photo
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16, textAlign: 'center' }}>
          Drag to align your face in the circle
        </div>

        {/* Drag area */}
        <div
          ref={containerRef}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          style={{
            width: 280, height: 280, borderRadius: 16, overflow: 'hidden',
            border: `2px solid ${isDragging ? '#4a9eff' : 'rgba(255,255,255,0.3)'}`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none', position: 'relative',
            touchAction: 'none',
          }}>
          <img
            src={preview} alt="Fighter"
            draggable={false}
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: position,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />
          {/* Face guide circle */}
          <div style={{
            position: 'absolute', top: '35%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 100, height: 100, borderRadius: '50%',
            border: '2px dashed rgba(255,255,255,0.8)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: 8, left: 0, right: 0,
            textAlign: 'center', fontSize: 11,
            color: 'rgba(255,255,255,0.6)',
            pointerEvents: 'none',
          }}>
            👆 Drag to reposition
          </div>
        </div>

        {/* Card preview */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Card preview</div>
            <div style={{ width: 70, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
              <img src={preview} alt="" draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: position }} />
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Avatar preview</div>
            <div style={{ width: 50, height: 50, borderRadius: '50%', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
              <img src={preview} alt="" draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: position }} />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={savePosition} disabled={saving} style={{
            background: '#1860a8', color: '#fff', border: 'none', borderRadius: 10,
            padding: '12px 28px', fontSize: 15, cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: 600,
          }}>
            {saving ? 'Saving...' : '✓ Save'}
          </button>
          <button onClick={() => setPositioning(false)} style={{
            background: 'transparent', color: 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10,
            padding: '12px 20px', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      {/* Preview circle */}
      <div style={{
        width: 100, height: 100, borderRadius: '50%', overflow: 'hidden',
        border: '2px solid #ddd', background: '#f4f6fb', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', cursor: 'pointer',
      }} onClick={() => !uploading && inputRef.current.click()}>
        {preview
          ? <img src={preview} alt="Fighter"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: position }} />
          : <div style={{ fontSize: 32, color: '#ccc' }}>📷</div>
        }
        {uploading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ color: '#fff', fontSize: 11 }}>Uploading...</div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={() => inputRef.current.click()} disabled={uploading} style={{
          background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8,
          padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          {preview ? 'Change photo' : 'Upload photo'}
        </button>
        {preview && (
          <button onClick={() => setPositioning(true)} style={{
            background: 'transparent', color: '#1860a8', border: '1px solid #1860a8',
            borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Reposition
          </button>
        )}
        {preview && (
          <button onClick={removePhoto} disabled={uploading} style={{
            background: 'transparent', color: '#c00', border: '1px solid #fcc',
            borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Remove
          </button>
        )}
      </div>

      {error && <div style={{ fontSize: 11, color: '#c00' }}>{error}</div>}
      <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center' }}>
        JPG or PNG · max 5MB · face photo works best
      </div>

      <input ref={inputRef} type="file" accept="image/*"
        style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}
